// src/table/AppClienteReactTable.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import jwt_decode from "jwt-decode";
import styled from "styled-components";
import { useSortBy, usePagination } from "react-table";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { Modal, Button, Table as BsTable, Spinner } from "react-bootstrap";

import ModalDocumentosCliente from "../components/ModalDocumentosCliente";
import ModalFormCliente from "../components/ModalFormCliente";
import Table from "./TableContainer";
import "../css/tablecomandas.css";

import inesLogo from "../images/ridersmiami-logo.png";

import {
  getClientes,
  getClienteById,
  deleteCliente,
} from "../helpers/rutaClientes";

const Styles = styled.div`
  /* Contenedor general: más ancho y con scroll prolijo */
  .table-container {
    width: 100%;
    max-height: calc(100vh - 230px);
    overflow: auto;
    border: 1px solid var(--app-border, #d9dee7);
    border-radius: 10px;
    background: var(--app-card, #fff);
  }

  /* La tabla queda con anchos fijos: evita que una columna empuje a otra */
  .table-container table {
    width: max-content !important;
    min-width: 1680px;
    table-layout: fixed !important;
    border-collapse: collapse !important;
    margin-bottom: 0 !important;
  }

  .table-container thead th {
    position: sticky;
    top: 0;
    z-index: 4;
    background: linear-gradient(180deg, #2f6fe4 0%, #214fae 100%) !important;
    color: #fff !important;
    font-size: 12px !important;
    font-weight: 700 !important;
    line-height: 1.15 !important;
    text-align: center !important;
    vertical-align: middle !important;
    padding: 7px 6px !important;
    border: 1px solid #1c469e !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  /* Oculta la fila de filtros por columna. Ya usamos el buscador general arriba. */
  .table-container thead tr:nth-child(2) {
    display: none !important;
  }

  .table-container tbody td {
    height: 40px;
    padding: 6px 8px !important;
    border: 1px solid var(--app-border-soft, #dde2e8) !important;
    vertical-align: middle !important;
    font-size: 13px !important;
    line-height: 1.2 !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    background: var(--app-table-row, #fff) !important;
    color: var(--app-text, #111827) !important;
  }

  .table-container tbody tr:nth-child(odd) td {
    background: var(--app-table-row-alt, #f8fafc) !important;
  }

  .table-container tbody tr:hover td {
    background: rgba(47, 128, 255, 0.12) !important;
  }

  html[data-theme="dark"] & .table-container {
    border-color: #2a3f60;
    background: #0f1b2d;
  }

  html[data-theme="dark"] & .table-container tbody td {
    background: #0f1b2d !important;
    color: #f8fafc !important;
    border-color: #2a3f60 !important;
  }

  html[data-theme="dark"] & .table-container tbody tr:nth-child(odd) td {
    background: #10213a !important;
  }

  html[data-theme="dark"] & .table-container tbody tr:nth-child(even) td {
    background: #0d1b31 !important;
  }

  html[data-theme="dark"] & .table-container tbody tr:hover td {
    background: #18345a !important;
  }

  html[data-theme="dark"] & .table-container tfoot td,
  html[data-theme="dark"] & .table-container tfoot th {
    background: #0b1628 !important;
    color: #f8fafc !important;
    border-color: #2a3f60 !important;
  }

  /* Anchos reales por columna */
  .table-container th:nth-child(1),
  .table-container td:nth-child(1) { width: 50px !important; min-width: 50px !important; max-width: 50px !important; }

  .table-container th:nth-child(2),
  .table-container td:nth-child(2) { width: 55px !important; min-width: 55px !important; max-width: 55px !important; }

  .table-container th:nth-child(3),
  .table-container td:nth-child(3) { width: 55px !important; min-width: 55px !important; max-width: 55px !important; }

  .table-container th:nth-child(4),
  .table-container td:nth-child(4) { width: 60px !important; min-width: 60px !important; max-width: 60px !important; }

  .table-container th:nth-child(5),
  .table-container td:nth-child(5) { width: 55px !important; min-width: 55px !important; max-width: 55px !important; }

  .table-container th:nth-child(6),
  .table-container td:nth-child(6) { width: 75px !important; min-width: 75px !important; max-width: 75px !important; text-align: center !important; }

  .table-container th:nth-child(7),
  .table-container td:nth-child(7) { width: 130px !important; min-width: 130px !important; max-width: 130px !important; }

  .table-container th:nth-child(8),
  .table-container td:nth-child(8) { width: 330px !important; min-width: 330px !important; max-width: 330px !important; text-align: left !important; }

  .table-container th:nth-child(9),
  .table-container td:nth-child(9) { width: 125px !important; min-width: 125px !important; max-width: 125px !important; }

  .table-container th:nth-child(10),
  .table-container td:nth-child(10) { width: 230px !important; min-width: 230px !important; max-width: 230px !important; text-align: left !important; }

  .table-container th:nth-child(11),
  .table-container td:nth-child(11) { width: 135px !important; min-width: 135px !important; max-width: 135px !important; text-align: left !important; }

  .table-container th:nth-child(12),
  .table-container td:nth-child(12) { width: 85px !important; min-width: 85px !important; max-width: 85px !important; text-align: center !important; }

  .table-container th:nth-child(13),
  .table-container td:nth-child(13) { width: 90px !important; min-width: 90px !important; max-width: 90px !important; text-align: center !important; }

  .table-container th:nth-child(14),
  .table-container td:nth-child(14) { width: 300px !important; min-width: 300px !important; max-width: 300px !important; text-align: left !important; }

  /* Botones centrados y todos del mismo tamaño */
  .table-container td:nth-child(-n + 5) {
    text-align: center !important;
    padding: 5px 4px !important;
  }

  .action-btn {
    width: 28px !important;
    height: 28px !important;
    padding: 0 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    border-radius: 5px !important;
    font-size: 13px !important;
    line-height: 1 !important;
    margin: 0 auto !important;
  }

  .action-btn i {
    line-height: 1 !important;
  }

  /* Paginación más compacta */
  .pagination,
  .table-pagination {
    margin-top: 8px;
    font-size: 13px;
  }
`;

/* ------------------------------- helpers -------------------------------- */

function norm(v) {
  return (v ?? "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

const digits = (v) => String(v ?? "").replace(/\D/g, "");

const clean = (v) => String(v ?? "").trim();

const getRoleUsuario = () => {
  const token = localStorage.getItem("token") || "";

  try {
    if (!token) return "";

    const decoded = jwt_decode(token);

    const usuario =
      decoded?.usuario ||
      decoded?.user ||
      decoded;

    return clean(usuario?.role).toUpperCase();
  } catch {
    return "";
  }
};

const fmtDate = (v) => {
  if (!v) return "-";

  const d = new Date(v);

  if (Number.isNaN(d.getTime())) return String(v);

  return d.toLocaleDateString("es-AR");
};

const textOrDash = (v) => {
  const s = String(v ?? "").trim();
  return s || "-";
};

const sanitizeFileName = (v) =>
  String(v ?? "")
    .trim()
    .replace(/[\\/:*?"<>|]+/g, "")
    .replace(/\s+/g, "_");

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const fitImageInBox = (img, maxW, maxH) => {
  const w = img?.naturalWidth || img?.width || maxW;
  const h = img?.naturalHeight || img?.height || maxH;
  const ratio = Math.min(maxW / w, maxH / h);

  return {
    w: w * ratio,
    h: h * ratio,
  };
};

const nombreCompletoCliente = (cliente = {}) => {
  const armado = [
    cliente?.apellido1,
    cliente?.apellido2,
    cliente?.nombre,
  ]
    .map(clean)
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

  return armado || clean(cliente?.razonsocial) || "-";
};

const direccionCompletaCliente = (cliente = {}) => {
  const armado = [
    cliente?.domicilio,
    cliente?.direccion2,
    cliente?.ciudad,
    cliente?.estado,
    cliente?.zipCode,
    cliente?.pais,
  ]
    .map(clean)
    .filter(Boolean)
    .join(" - ")
    .replace(/\s+/g, " ")
    .trim();

  return armado || "-";
};

const esClienteComprador = (cliente = {}) => {
  // Los clientes viejos no tienen esComprador, pero para nosotros son compradores.
  return cliente.esComprador !== false;
};

const esClienteGarante = (cliente = {}) => {
  return cliente.esGarante === true;
};

const tipoClienteTexto = (cliente = {}) => {
  const comprador = esClienteComprador(cliente);
  const garante = esClienteGarante(cliente);

  if (comprador && garante) return "COMPRADOR / GARANTE";
  if (garante) return "GARANTE";
  return "COMPRADOR";
};

const filtrarPorTipoCliente = (cliente = {}, tipo = "todos") => {
  if (tipo === "garante") return esClienteGarante(cliente);
  if (tipo === "comprador") return esClienteComprador(cliente);
  return true;
};

const tipoClienteBadgeStyle = (cliente = {}) => {
  const tipo = tipoClienteTexto(cliente);

  if (tipo === "GARANTE") {
    return {
      background: "#f59e0b",
      color: "#111827",
      border: "1px solid #facc15",
    };
  }

  if (tipo === "COMPRADOR / GARANTE") {
    return {
      background: "#8b5cf6",
      color: "#ffffff",
      border: "1px solid #a78bfa",
    };
  }

  return {
    background: "#0d6efd",
    color: "#ffffff",
    border: "1px solid #60a5fa",
  };
};

const getClienteFromResponse = (resp, fallback = null) => {
  if (resp?.cliente) return resp.cliente;
  if (resp?.data?.cliente) return resp.data.cliente;
  if (resp?.data) return resp.data;
  return resp || fallback;
};

// dedupe defensivo
function uniqById(arr) {
  const m = new Map();

  for (const it of arr || []) {
    const k = it?._id ?? it?.codcli ?? JSON.stringify(it);

    if (!m.has(k)) {
      m.set(k, it);
    }
  }

  return Array.from(m.values());
}

// debounce liviano
function useDebounce(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return debounced;
}

/* ------------------------------------------------------------------------ */

const btnActionSmall = {
  width: 28,
  height: 28,
  padding: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 4,
  fontSize: 13,
};

export default function AppClienteReactTable() {

  const roleUsuario = getRoleUsuario();

  const puedeBorrar = [
    "ROOT_ROLE",
    "ADMIN_ROLE",
  ].includes(roleUsuario);

  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showEdit, setShowEdit] = useState(false);
  const [clienteEdit, setClienteEdit] = useState(null);

  const [showDocs, setShowDocs] = useState(false);
  const [clienteDocs, setClienteDocs] = useState(null);
  const [docsLoadingId, setDocsLoadingId] = useState(null);

  const [pdfLoadingId, setPdfLoadingId] = useState(null);

  const [showFicha, setShowFicha] = useState(false);
  const [fichaData, setFichaData] = useState(null);
  const [fichaLoadingId, setFichaLoadingId] = useState(null);

  const [q, setQ] = useState("");
  const dq = useDebounce(q, 250);

  const [tipoVista, setTipoVista] = useState("todos");

  const [progress, setProgress] = useState({
    loaded: 0,
    total: 0,
  });

  const seqRef = useRef(0);
  const abortRef = useRef(null);

  const fetchAll = useCallback(async () => {
    try {
      abortRef.current?.abort?.();
    } catch (_) {}

    const controller = new AbortController();
    abortRef.current = controller;

    const seq = ++seqRef.current;

    setLoading(true);
    setAll([]);
    setProgress({ loaded: 0, total: 0 });

    const LIMIT = 200;

    try {
      const cargarPaginas = async (tipo = "") => {
        const first = await getClientes({
          page: 1,
          limit: LIMIT,
          q: "",
          tipo,
          signal: controller.signal,
        });

        let accLocal = [...(first?.clientes || [])];
        accLocal = uniqById(accLocal);

        const totalLocal = Number(first?.total || accLocal.length || 0);
        const pages = Math.max(1, Math.ceil(totalLocal / LIMIT));

        for (let p = 2; p <= pages; p++) {
          const r = await getClientes({
            page: p,
            limit: LIMIT,
            q: "",
            tipo,
            signal: controller.signal,
          });

          accLocal = uniqById(accLocal.concat(r?.clientes || []));
        }

        return accLocal;
      };

      // Primero traemos lo normal. Esto mantiene compatibilidad con clientes viejos.
      const base = await cargarPaginas("");

      if (seq !== seqRef.current) return;

      setAll(base);
      setProgress({
        loaded: base.length,
        total: base.length,
      });

      // Después traemos garantes explícitamente.
      const garantes = await cargarPaginas("garante");

      if (seq !== seqRef.current) return;

      const final = uniqById([...base, ...garantes]);

      setAll(final);
      setProgress({
        loaded: final.length,
        total: final.length,
      });
    }
 catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;

      console.error("❌ Error cargando clientes:", e);

      setAll([]);
      setProgress({
        loaded: 0,
        total: 0,
      });
    } finally {
      if (seq === seqRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchAll();

    return () => {
      try {
        abortRef.current?.abort?.();
      } catch (_) {}
    };
  }, [fetchAll]);

  const borrar = useCallback(async (mongoId) => {
    if (!mongoId) {
      alert("No se encontró el ID del cliente.");
      return;
    }

    if (!window.confirm("¿Eliminar cliente?")) return;

    try {
      await deleteCliente(mongoId);

      setAll((prev) => prev.filter((c) => c?._id !== mongoId));
    } catch (e) {
      console.error("❌ Error deleteCliente:", e);
      alert("No se pudo borrar el cliente.");
    }
  }, []);

  const abrirEdicion = useCallback((cliente) => {
    setClienteEdit(cliente);
    setShowEdit(true);
  }, []);

  const cerrarEdicion = useCallback(() => {
    setShowEdit(false);
    setClienteEdit(null);
  }, []);

  const abrirDocumentos = useCallback(async (clienteRow) => {
  if (!clienteRow?._id) {
    alert("No se encontró el ID del cliente.");
    return;
  }

  try {
    setDocsLoadingId(clienteRow._id);
    setClienteDocs(clienteRow);
    setShowDocs(true);

    const resp = await getClienteById(clienteRow._id);
    const clienteCompleto = getClienteFromResponse(resp, clienteRow);

    setClienteDocs(clienteCompleto);
  } catch (e) {
    console.error("❌ Error cargando documentos del cliente:", e);
    setClienteDocs(clienteRow);
  } finally {
    setDocsLoadingId(null);
  }
    }, []);

const cerrarDocumentos = useCallback(() => {
  setShowDocs(false);
  setClienteDocs(null);
}, []);

const actualizarClienteEnListado = useCallback((clienteActualizado) => {
  if (!clienteActualizado?._id) return;

  setAll((prev) =>
    (prev || []).map((c) =>
      c?._id === clienteActualizado._id ? clienteActualizado : c
    )
  );

  setClienteDocs(clienteActualizado);
}, []);


  const cargarFichaData = useCallback(async (clienteRow) => {
    const safe = async (label, fn, fallback) => {
      try {
        return await fn();
      } catch (e) {
        console.error(`❌ Error cargando ${label}:`, e);
        return fallback;
      }
    };

    const clienteResp = await safe(
      "cliente por ID",
      () => getClienteById(clienteRow._id),
      clienteRow
    );

    const cliente = getClienteFromResponse(clienteResp, clienteRow);
    const fechaEmision = new Date();

    return {
      cliente,
      fechaEmision,
    };
  }, []);

  const abrirFichaCliente = useCallback(
    async (clienteRow) => {
      if (!clienteRow?._id) {
        alert("No se encontró el ID del cliente.");
        return;
      }

      try {
        setFichaLoadingId(clienteRow._id);
        setFichaData(null);
        setShowFicha(true);

        const data = await cargarFichaData(clienteRow);
        setFichaData(data);
      } catch (e) {
        console.error("❌ Error abriendo ficha del cliente:", e);

        setFichaData({
          cliente: clienteRow,
          fechaEmision: new Date(),
        });
      } finally {
        setFichaLoadingId(null);
      }
    },
    [cargarFichaData]
  );

  const generarPdfFicha = useCallback(
    async (clienteRow) => {
      if (!clienteRow?._id) return;

      try {
        setPdfLoadingId(clienteRow._id);

        const data = await cargarFichaData(clienteRow);

        const { cliente, fechaEmision } = data;

        const doc = new jsPDF({
          orientation: "p",
          unit: "mm",
          format: "a4",
        });

        const pageWidth = doc.internal.pageSize.getWidth();

        try {
          const logo = await loadImage(inesLogo);
          const box = fitImageInBox(logo, 34, 20);

          doc.addImage(logo, "PNG", 10, 7, box.w, box.h);
        } catch (e) {
          console.warn("No se pudo cargar el logo para el PDF:", e);
        }

        doc.setFont("times", "bold");
        doc.setFontSize(20);
        doc.text("FICHA DEL CLIENTE QUE COMPRA", pageWidth / 2, 16, {
          align: "center",
        });

        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.text(`N°: ${textOrDash(cliente?.codcli)}`, 190, 16, {
          align: "right",
        });

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text(`Fecha emisión: ${fmtDate(fechaEmision)}`, 190, 22, {
          align: "right",
        });

        doc.setDrawColor(60);
        doc.setLineWidth(0.3);
        doc.line(10, 30, 200, 30);

        autoTable(doc, {
          startY: 34,
          theme: "grid",
          styles: {
            fontSize: 8.8,
            cellPadding: 2,
            valign: "middle",
            overflow: "linebreak",
          },
          columnStyles: {
            0: { cellWidth: 30, fontStyle: "bold" },
            1: { cellWidth: 65 },
            2: { cellWidth: 30, fontStyle: "bold" },
            3: { cellWidth: 65 },
          },
          body: [
            [
              "Código",
              textOrDash(cliente?.codcli),
              "Fecha Ingreso",
              fmtDate(cliente?.fecing),
            ],
            [
              "ID / Documento",
              textOrDash(cliente?.dni),
              "País",
              textOrDash(cliente?.pais || "USA"),
            ],
            [
              "Apellido 1",
              textOrDash(cliente?.apellido1),
              "Apellido 2",
              textOrDash(cliente?.apellido2),
            ],
            [
              "Nombre",
              textOrDash(cliente?.nombre),
              "Nombre completo",
              textOrDash(nombreCompletoCliente(cliente)),
            ],
            [
              "Teléfono",
              textOrDash(cliente?.telefono1),
              "Email",
              textOrDash(cliente?.email),
            ],
            [
              "Domicilio",
              textOrDash(cliente?.domicilio),
              "Apt / Suite",
              textOrDash(cliente?.direccion2),
            ],
            [
              "Ciudad",
              textOrDash(cliente?.ciudad),
              "Estado",
              textOrDash(cliente?.estado),
            ],
            [
              "ZIP Código",
              textOrDash(cliente?.zipCode),
              "Dirección completa",
              textOrDash(direccionCompletaCliente(cliente)),
            ],
          ],
        });

        doc.save(
          `Ficha_Cliente_${textOrDash(cliente?.codcli)}_${sanitizeFileName(
            nombreCompletoCliente(cliente)
          )}.pdf`
        );
      } catch (e) {
        console.error("❌ Error generando PDF de ficha:", e);
        alert("No se pudo generar el PDF de la ficha del cliente.");
      } finally {
        setPdfLoadingId(null);
      }
    },
    [cargarFichaData]
  );

  const dataFiltrada = useMemo(() => {
    const raw = String(dq ?? "").trim();

    const base = (all || []).filter((c) =>
      filtrarPorTipoCliente(c, tipoVista)
    );

    if (!raw) return base;

    const hasLetters = /[a-zA-ZáéíóúñüÁÉÍÓÚÑÜ]/.test(raw);
    const qText = norm(raw);
    const qNum = digits(raw);

    return (all || []).filter((c) => {
      const cod = digits(c?.codcli);
      const idDoc = digits(c?.dni);
      const tel1 = digits(c?.telefono1);
      const zip = digits(c?.zipCode);

      const nombre = norm(nombreCompletoCliente(c));
      const email = norm(c?.email);
      const ciudad = norm(c?.ciudad);
      const estado = norm(c?.estado);
      const pais = norm(c?.pais);
      const domicilio = norm(c?.domicilio);
      const direccion2 = norm(c?.direccion2);

      if (hasLetters) {
        return (
          nombre.includes(qText) ||
          email.includes(qText) ||
          ciudad.includes(qText) ||
          estado.includes(qText) ||
          pais.includes(qText) ||
          domicilio.includes(qText) ||
          direccion2.includes(qText)
        );
      }

      if (!qNum) return false;

      if (qNum.length >= 7) {
        return idDoc === qNum || cod === qNum || tel1 === qNum;
      }

      return (
        idDoc.startsWith(qNum) ||
        cod.startsWith(qNum) ||
        tel1.startsWith(qNum) ||
        zip.startsWith(qNum)
      );
    });
  }, [all, dq, tipoVista]);

  const columns = useMemo(
    () => [
      {
        id: "editar",
        Header: "Edita",
        accessor: "_id",
        width: 50,
        Cell: ({ row }) => (
          <button
            className="action-btn action-btn--edit"
            title="Editar"
            onClick={() => abrirEdicion(row.original)}
          >
            <i className="fa fa-pencil" aria-hidden="true" />
          </button>
        ),
      },
      {
        id: "verFicha",
        Header: "Ver",
        accessor: "_id",
        width: 55,
        Cell: ({ row }) => (
        <button
          className="action-btn"
          title="Ver ficha del cliente"
          onClick={() => abrirFichaCliente(row.original)}
          disabled={fichaLoadingId === row.original._id}
          style={{
            ...btnActionSmall,
            backgroundColor: "#1565c0",
            color: "#fff",
            border: "none",
            cursor:
              fichaLoadingId === row.original._id ? "not-allowed" : "pointer",
            opacity: fichaLoadingId === row.original._id ? 0.7 : 1,
          }}
        >
          <i className="fa fa-eye" aria-hidden="true" />
        </button>
        ),
      },
      {
        id: "pdf",
        Header: "PDF",
        accessor: "_id",
        width: 55,
        Cell: ({ row }) => (
        <button
          className="action-btn"
          title="Generar ficha PDF"
          onClick={() => generarPdfFicha(row.original)}
          disabled={pdfLoadingId === row.original._id}
          style={{
            ...btnActionSmall,
            backgroundColor: "#b71c1c",
            color: "#fff",
            border: "none",
            cursor:
              pdfLoadingId === row.original._id ? "not-allowed" : "pointer",
            opacity: pdfLoadingId === row.original._id ? 0.7 : 1,
          }}
        >
          <i className="fa fa-file-pdf-o" aria-hidden="true" />
        </button>
        ),
      },

      {
        id: "docs",
        Header: "Docs",
        accessor: "_id",
        width: 60,
        Cell: ({ row }) => {
          const documentos = row.original?.documentos || {};
          const tieneDocs = !!(
            documentos?.frente?.url ||
            documentos?.dorso?.url ||
            documentos?.pdf?.url
          );

          return (
            <button
              className="action-btn"
              title={tieneDocs ? "Ver documentos cargados" : "Cargar documentos"}
              onClick={() => abrirDocumentos(row.original)}
              disabled={docsLoadingId === row.original._id}
              style={{
                ...btnActionSmall,
                backgroundColor: tieneDocs ? "#2e7d32" : "#6c757d",
                color: "#fff",
                border: "none",
                cursor:
                  docsLoadingId === row.original._id ? "not-allowed" : "pointer",
                opacity: docsLoadingId === row.original._id ? 0.7 : 1,
              }}
            >
              <i
                className={`fa ${tieneDocs ? "fa-id-card-o" : "fa-paperclip"}`}
                aria-hidden="true"
              />
            </button>
          );
        },
      },

      {
        id: "eliminar",
        Header: puedeBorrar ? "Borrar" : "",
        accessor: "_id",
        width: 50,
        Cell: ({ row }) =>
          puedeBorrar ? (
            <button
              className="action-btn action-btn--del"
              title="Eliminar"
              onClick={() => borrar(row.original._id)}
            >
              <i className="fa fa-trash-o" aria-hidden="true" />
            </button>
          ) : null,
      },

      {
        Header: "Código",
        accessor: "codcli",
        width: 65,
        minWidth: 55,
        maxWidth: 90,
      },
      {
        Header: "Tipo",
        id: "tipoCliente",
        accessor: (row) => tipoClienteTexto(row),
        width: 120,
        minWidth: 100,
        maxWidth: 150,
        Cell: ({ row }) => (
          <span
            style={{
              display: "inline-block",
              padding: "3px 8px",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 900,
              lineHeight: 1.1,
              whiteSpace: "nowrap",
              ...tipoClienteBadgeStyle(row.original),
            }}
          >
            {tipoClienteTexto(row.original)}
          </span>
        ),
      },
      {
        Header: "Cliente",
        accessor: (row) => nombreCompletoCliente(row),
        id: "clienteNombre",
        width: 310,
        minWidth: 220,
        Cell: ({ value }) => (
          <div
            style={{
              textAlign: "left",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={value}
          >
            {value}
          </div>
        ),
      },
      {
        Header: "Teléfono",
        accessor: "telefono1",
        width: 120,
        minWidth: 100,
        maxWidth: 170,
      },
      {
        Header: "Email",
        accessor: "email",
        width: 220,
        minWidth: 180,
        Cell: ({ value }) => (
          <div
            style={{
              textAlign: "left",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={value || ""}
          >
            {value || "-"}
          </div>
        ),
      },
      {
        Header: "Ciudad",
        accessor: "ciudad",
        width: 130,
        minWidth: 100,
      },
      {
        Header: "Estado",
        accessor: "estado",
        width: 80,
        minWidth: 70,
        maxWidth: 110,
      },
      {
        Header: "ZIP",
        accessor: "zipCode",
        width: 90,
        minWidth: 70,
        maxWidth: 120,
      },
      {
        Header: "Dirección",
        accessor: (row) => direccionCompletaCliente(row),
        id: "direccionCompleta",
        width: 300,
        minWidth: 220,
        Cell: ({ value }) => (
          <div
            style={{
              textAlign: "left",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={value}
          >
            {value}
          </div>
        ),
      },
    ],
    [
      abrirEdicion,
      abrirFichaCliente,
      abrirDocumentos,
      borrar,
      generarPdfFicha,
      docsLoadingId,
      fichaLoadingId,
      pdfLoadingId,
      puedeBorrar,
    ]
  );

  const tableKey = useMemo(
    () => `${dq}__${tipoVista}__${all.length}`,
    [dq, tipoVista, all.length]
  );

return (
  <>
    <div className="container-xl mt-4 clientes-table-page">
      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <h3 className="text-center mb-3">LISTADO DE CLIENTES / GARANTES</h3>

          {/* ---------------- BUSCADOR ---------------- */}
          <div className="mb-3">
            <div className="d-flex align-items-end justify-content-between gap-3 flex-wrap">
              <div className="d-flex align-items-end gap-3 flex-wrap">
                <div style={{ minWidth: 340 }}>
                  <label className="form-label mb-0 small">Buscar</label>
                  <input
                    className="form-control form-control-sm"
                    placeholder="nombre / ID / teléfono / email / ciudad / estado / ZIP"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                  />
                </div>

                <div style={{ minWidth: 180 }}>
                  <label className="form-label mb-0 small">Tipo</label>
                  <select
                    className="form-control form-control-sm"
                    value={tipoVista}
                    onChange={(e) => setTipoVista(e.target.value)}
                  >
                    <option value="todos">Todos</option>
                    <option value="comprador">Compradores</option>
                    <option value="garante">Garantes</option>
                  </select>
                </div>

                <div
                  className="small text-muted mb-1"
                  style={{ whiteSpace: "nowrap" }}
                >
                  {loading
                    ? `Cargando ${progress.loaded} / ${
                        progress.total || "?"
                      }...`
                    : `Mostrando ${dataFiltrada.length} de ${all.length}`}
                </div>
              </div>

              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-secondary btn-sm px-3"
                  style={{ fontSize: 14 }}
                  onClick={() => {
                    setQ("");
                    setTipoVista("todos");
                  }}
                >
                  Limpiar
                </button>

                <button
                  className="btn btn-outline-primary btn-sm px-3"
                  style={{ fontSize: 14 }}
                  onClick={fetchAll}
                  disabled={loading}
                >
                  Recargar
                </button>
              </div>
            </div>
          </div>

          {/* ---------------- TABLA ---------------- */}
          <Styles>
            <div className="table-container">
              <Table
                key={tableKey}
                columns={columns}
                data={dataFiltrada}
                loading={loading}
                plugins={[useSortBy, usePagination]}
              />
            </div>
          </Styles>
        </div>
      </div>
    </div>

    {/* ---------------- MODAL EDICIÓN ---------------- */}
    <ModalFormCliente
      show={showEdit}
      cliente={clienteEdit}
      handleClose={cerrarEdicion}
      onSaved={fetchAll}
    />

    {/* ---------------- MODAL DOCUMENTOS CLIENTE ---------------- */}
    <ModalDocumentosCliente
      show={showDocs}
      cliente={clienteDocs}
      onHide={cerrarDocumentos}
      onClienteActualizado={actualizarClienteEnListado}
    />

    {/* ---------------- MODAL FICHA CLIENTE ---------------- */}
    <Modal
      show={showFicha}
      onHide={() => setShowFicha(false)}
      size="xl"
      centered
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title>Ficha del Cliente</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {fichaLoadingId && !fichaData ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
            <div className="mt-2">Cargando ficha...</div>
          </div>
        ) : fichaData ? (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                borderBottom: "1px solid #333",
                paddingBottom: 12,
                marginBottom: 16,
              }}
            >
              <div style={{ width: 160 }}>
                <img
                  src={inesLogo}
                  alt="Logo"
                  style={{
                    maxWidth: 130,
                    maxHeight: 55,
                    objectFit: "contain",
                  }}
                />
              </div>

              <div
                style={{
                  flex: 1,
                  textAlign: "center",
                  fontFamily: "serif",
                  fontWeight: "bold",
                  fontSize: 28,
                }}
              >
                FICHA DEL CLIENTE QUE COMPRA
              </div>

              <div style={{ width: 170, textAlign: "right" }}>
                <div style={{ fontWeight: "bold", fontSize: 18 }}>
                  N°: {textOrDash(fichaData.cliente?.codcli)}
                </div>

                <div style={{ fontSize: 13, marginTop: 8 }}>
                  Fecha emisión: {fmtDate(fichaData.fechaEmision)}
                </div>
              </div>
            </div>

            <BsTable bordered size="sm" responsive style={{ fontSize: 13 }}>
              <tbody>
                <tr>
                  <th style={{ width: "15%" }}>Código</th>
                  <td style={{ width: "35%" }}>
                    {textOrDash(fichaData.cliente?.codcli)}
                  </td>

                  <th style={{ width: "15%" }}>Fecha Ingreso</th>
                  <td style={{ width: "35%" }}>
                    {fmtDate(fichaData.cliente?.fecing)}
                  </td>
                </tr>

                <tr>
                  <th>ID / Documento</th>
                  <td>{textOrDash(fichaData.cliente?.dni)}</td>

                  <th>País</th>
                  <td>{textOrDash(fichaData.cliente?.pais || "USA")}</td>
                </tr>

                <tr>
                  <th>Apellido 1</th>
                  <td>{textOrDash(fichaData.cliente?.apellido1)}</td>

                  <th>Apellido 2</th>
                  <td>{textOrDash(fichaData.cliente?.apellido2)}</td>
                </tr>

                <tr>
                  <th>Nombre</th>
                  <td>{textOrDash(fichaData.cliente?.nombre)}</td>

                  <th>Nombre completo</th>
                  <td>{textOrDash(nombreCompletoCliente(fichaData.cliente))}</td>
                </tr>

                <tr>
                  <th>Teléfono</th>
                  <td>{textOrDash(fichaData.cliente?.telefono1)}</td>

                  <th>Email</th>
                  <td>{textOrDash(fichaData.cliente?.email)}</td>
                </tr>

                <tr>
                  <th>Domicilio</th>
                  <td>{textOrDash(fichaData.cliente?.domicilio)}</td>

                  <th>Apt / Suite</th>
                  <td>{textOrDash(fichaData.cliente?.direccion2)}</td>
                </tr>

                <tr>
                  <th>Ciudad</th>
                  <td>{textOrDash(fichaData.cliente?.ciudad)}</td>

                  <th>Estado</th>
                  <td>{textOrDash(fichaData.cliente?.estado)}</td>
                </tr>

                <tr>
                  <th>ZIP Código</th>
                  <td>{textOrDash(fichaData.cliente?.zipCode)}</td>

                  <th>Dirección completa</th>
                  <td>
                    {textOrDash(direccionCompletaCliente(fichaData.cliente))}
                  </td>
                </tr>
              </tbody>
            </BsTable>
          </>
        ) : (
          <div className="text-muted">Sin datos para mostrar.</div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {fichaData?.cliente && (
          <Button
            variant="danger"
            onClick={() => generarPdfFicha(fichaData.cliente)}
            disabled={pdfLoadingId === fichaData.cliente?._id}
          >
            PDF
          </Button>
        )}

        <Button variant="secondary" onClick={() => setShowFicha(false)}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  </>
);
}